import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton, Button, TextField,
  ToggleButton, ToggleButtonGroup, Alert, CircularProgress, MenuItem,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  Close, BugReport, Lightbulb, Send, CheckCircle, ArrowBack,
} from '@mui/icons-material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

const DEVICE_OPTIONS = ['Desktop', 'Laptop', 'Tablet', 'Phone'];
const BROWSER_OPTIONS = ['Chrome', 'Edge', 'Firefox', 'Safari', 'Other'];

function FeedbackDialog({ open, onClose, user, initialType }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [type, setType] = useState(initialType || 'issue');

  useEffect(() => {
    if (open && initialType) setType(initialType);
  }, [open, initialType]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expected, setExpected] = useState('');
  const [device, setDevice] = useState('');
  const [browser, setBrowser] = useState('');
  const [osInfo, setOsInfo] = useState('');
  const [screenshot, setScreenshot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const resetForm = () => {
    setType('issue');
    setTitle('');
    setDescription('');
    setStepsToReproduce('');
    setExpected('');
    setDevice('');
    setBrowser('');
    setOsInfo('');
    setScreenshot('');
    setResult(null);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Build Gmail compose URL — opens directly in browser since user is signed in with Google
  const buildGmailComposeUrl = () => {
    const prefix = type === 'issue' ? '[Ekamanam Issue] ' : '[Ekamanam Feedback] ';
    const subject = `${prefix}${title.trim()}`;

    let body = '';
    if (type === 'issue') {
      body += `Describe the bug:\n${description.trim()}\n\n`;
      if (stepsToReproduce.trim()) body += `Steps to Reproduce:\n${stepsToReproduce.trim()}\n\n`;
      if (expected.trim()) body += `Expected behavior:\n${expected.trim()}\n\n`;
      if (screenshot.trim()) body += `Screenshots:\n${screenshot.trim()}\n\n`;
      if (device || browser || osInfo.trim()) {
        body += `Device Information:\n`;
        if (osInfo.trim()) body += `- OS: ${osInfo.trim()}\n`;
        if (browser) body += `- Browser: ${browser}\n`;
        if (device) body += `- Device: ${device}\n`;
        body += '\n';
      }
    } else {
      body += `Feedback:\n${description.trim()}\n\n`;
      if (expected.trim()) body += `Desired improvement:\n${expected.trim()}\n\n`;
      if (stepsToReproduce.trim()) body += `Alternatives considered:\n${stepsToReproduce.trim()}\n\n`;
    }
    if (user?.email) body += `\n---\nSubmitted by: ${user.displayName || ''} (${user.email})\n`;

    const to = 'admin@ekamanam.com,amandeep.talwar@gmail.com';
    return `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) return;
    setSubmitting(true);
    setError(null);

    try {
      // Save to Firestore
      if (db) {
        await addDoc(collection(db, 'feedback'), {
          type,
          title: title.trim(),
          description: description.trim(),
          stepsToReproduce: stepsToReproduce.trim() || null,
          expected: expected.trim() || null,
          device: device || null,
          browser: browser || null,
          osInfo: osInfo.trim() || null,
          screenshot: screenshot.trim() || null,
          userEmail: user?.email || null,
          userId: user?.uid || null,
          createdAt: serverTimestamp(),
          status: 'new',
        });
      }

      // Open Gmail compose with pre-filled details
      window.open(buildGmailComposeUrl(), '_blank', 'noopener,noreferrer');

      setResult({ success: true });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = title.trim() && description.trim() && !submitting;

  // Success view
  if (result?.success) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircle sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.7 }}>
            Your feedback has been recorded and a Gmail compose window has been opened with all the details. Please click <strong>Send</strong> in Gmail to complete the submission.
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 3 }}>
            If Gmail didn't open, check your pop-up blocker or email your feedback directly to admin@ekamanam.com
          </Typography>
          <Button variant="contained" onClick={handleClose} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Done
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider',
      }}>
        {isMobile && (
          <IconButton onClick={handleClose} size="small" edge="start">
            <ArrowBack />
          </IconButton>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>Send Feedback</Typography>
          <Typography variant="caption" color="text.secondary">Help us improve Ekamanam</Typography>
        </Box>
        {!isMobile && (
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        )}
      </Box>

      <DialogContent sx={{ p: isMobile ? 2 : 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Type toggle */}
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>What would you like to share?</Typography>
          <ToggleButtonGroup
            value={type}
            exclusive
            onChange={(_, v) => { if (v) setType(v); }}
            fullWidth
            size="small"
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, borderRadius: 2, gap: 0.75 } }}
          >
            <ToggleButton value="issue" color="error">
              <BugReport sx={{ fontSize: 18 }} /> Report Issue
            </ToggleButton>
            <ToggleButton value="feedback" color="success">
              <Lightbulb sx={{ fontSize: 18 }} /> Feedback / Feature Request
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Title */}
        <TextField
          label="Title"
          placeholder={type === 'issue' ? 'Brief summary of the issue' : 'Brief summary of your suggestion'}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          size="small"
        />

        {/* Description */}
        <TextField
          label={type === 'issue' ? 'Describe the issue' : 'Describe your feedback'}
          placeholder={type === 'issue'
            ? 'A clear description of what the bug is...'
            : 'A clear description of the improvement you\'d like...'}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          required
          multiline
          minRows={3}
          maxRows={6}
          size="small"
        />

        {/* Steps to reproduce / Alternatives considered */}
        <TextField
          label={type === 'issue' ? 'Steps to Reproduce' : 'Alternatives Considered'}
          placeholder={type === 'issue'
            ? '1. Go to...\n2. Click on...\n3. See error'
            : 'Any alternative solutions or features you\'ve considered...'}
          value={stepsToReproduce}
          onChange={(e) => setStepsToReproduce(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          size="small"
        />

        {/* Expected behavior / Desired improvement */}
        <TextField
          label={type === 'issue' ? 'Expected Behavior' : 'Desired Improvement'}
          placeholder={type === 'issue'
            ? 'What you expected to happen...'
            : 'What you want to happen...'}
          value={expected}
          onChange={(e) => setExpected(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          size="small"
        />

        {/* Device info — only for issues */}
        {type === 'issue' && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Device"
              value={device}
              onChange={(e) => setDevice(e.target.value)}
              size="small"
              sx={{ minWidth: 130, flex: 1 }}
            >
              <MenuItem value="">--</MenuItem>
              {DEVICE_OPTIONS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Browser"
              value={browser}
              onChange={(e) => setBrowser(e.target.value)}
              size="small"
              sx={{ minWidth: 130, flex: 1 }}
            >
              <MenuItem value="">--</MenuItem>
              {BROWSER_OPTIONS.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
            </TextField>
            <TextField
              label="OS"
              placeholder="e.g. Windows 11, macOS, Android"
              value={osInfo}
              onChange={(e) => setOsInfo(e.target.value)}
              size="small"
              sx={{ minWidth: 130, flex: 1 }}
            />
          </Box>
        )}

        {/* Screenshot URL */}
        <TextField
          label="Screenshot URL (optional)"
          placeholder="Paste a link to a screenshot if applicable"
          value={screenshot}
          onChange={(e) => setScreenshot(e.target.value)}
          fullWidth
          size="small"
        />

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        )}

        {/* Submit */}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send />}
          sx={{ alignSelf: isMobile ? 'stretch' : 'flex-end', borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackDialog;
